import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {LevelType} from 'app/original-models/level-type.model';
import {STATE_NAMES, TRANSACTIONS_STATES} from 'app/common/state-names';
import {StartingPage} from 'app/common/constants';
import {ACCESS, arApRoles, todayOnlyRoles} from 'app/original-models/access.model';

/** Landing state for each starting-page preference. Insights is the 1st Today tab, Outlook the 2nd. */
export const STARTING_PAGE_STATES: Record<StartingPage, string> = {
    [StartingPage.Home]: STATE_NAMES.home,
    [StartingPage.Insights]: STATE_NAMES.insights,
    [StartingPage.Outlook]: STATE_NAMES.insightsOutlookTab,
    [StartingPage.Transactions]: TRANSACTIONS_STATES.transactionsPage,
    [StartingPage.AccountReceivables]: STATE_NAMES.accountReceivables,
    [StartingPage.Planning]: STATE_NAMES.planning
};

@Injectable({ providedIn: 'root' })
export class DefaultRouteService {
    getLoginRoute(storage: any, permissionsService: any, goToLevel: LevelType) {
        if (permissionsService.isOwner && !permissionsService.checkPermission('setupConnectedAccounts')?.view) {
            return STATE_NAMES.settingsInvitations;
        }
        return this.getDefaultRoute(storage, permissionsService, goToLevel);
    }

    /**
     * Starting pages the current user is allowed to land on, in the order they should be offered.
     * Single source of truth for both the redirect below and the profile starting-page picker.
     */
    getAllowedStartingPages(permissionsService: any): StartingPage[] {
        const role = permissionsService.currentRole || '';
        // Category roles are confined to Today, so there is nothing to offer them.
        if (todayOnlyRoles.indexOf(role.toLowerCase()) >= 0
            || role.toLowerCase() === ACCESS.GroupReviewer.toLowerCase()) {
            return [];
        }
        const canView = (key: string): boolean => !!permissionsService.checkPermission(key)?.view;
        const canViewToday = canView(STATE_NAMES.dashboard);
        const pages: StartingPage[] = [];

        pages.push(StartingPage.Home);
        if (canViewToday) {
            pages.push(StartingPage.Insights, StartingPage.Outlook);
        }
        if (role !== ACCESS.REVIEWER && canView('transactionsNew')) {
            pages.push(StartingPage.Transactions);
        }
        if (canView('accountReceivables')) {
            pages.push(StartingPage.AccountReceivables);
        }
        if (arApRoles.indexOf(role.toLowerCase()) < 0
            && (permissionsService.isOwner || permissionsService.checkMultiplePermission(STATE_NAMES.planning, [
                'scenario',
                'transactionsMapped',
                'committedPlans'
            ], true)?.view)) {
            pages.push(StartingPage.Planning);
        }
        return pages;
    }

    /**
     * Landing route resolved once permissions match the selected company/group. Prefer this over
     * {@link getDefaultRoute} after a company switch: the permission reload is asynchronous, so
     * resolving straight away would answer from the company the user just left.
     */
    getDefaultRoute$(storage: any, permissionsService: any, goToLevel: LevelType): Observable<string> {
        return permissionsService.awaitPermissionsReady$()
            .pipe(map(() => this.getDefaultRoute(storage, permissionsService, goToLevel)));
    }

    getDefaultRoute(storage: any, permissionsService: any, goToLevel: LevelType) {
        if (storage.todayOnly) {
            return STATE_NAMES.insights;
        }
        const startingPage = storage.startingPage;
        const allowedPages = this.getAllowedStartingPages(permissionsService);

        // Group level always lands on Home, whatever the preference, when the feed is permitted.
        if (goToLevel === LevelType.Group && allowedPages.includes(StartingPage.Home)) {
            return STATE_NAMES.home;
        }

        // Preference is honored only when its permission is granted.
        if (allowedPages.includes(startingPage)) {
            return STARTING_PAGE_STATES[startingPage];
        }

        // Fallback chain: transactions -> account-receivables -> home, restricted roles skipped.
        return allowedPages.includes(StartingPage.Transactions) ? TRANSACTIONS_STATES.transactionsPage
            : allowedPages.includes(StartingPage.AccountReceivables) ? STATE_NAMES.accountReceivables
            : STATE_NAMES.home;
    }
}
